import type { DroneAlertThresholds } from '@shared/types';

type ThresholdKey = keyof DroneAlertThresholds;

/** 指标方向，语义与 `@shared/alerts` 的 `MetricSpec.direction` 对齐。 */
export type ThresholdDirection = 'lower' | 'upper';

export interface ThresholdGroup {
  key: string;
  title: string;
  description: string;
  direction: ThresholdDirection;
  metrics: { prefix: string; label: string; unit: string }[];
}

/** 阈值分组：`metrics[].prefix` 对应 `DroneAlertThresholds` 里的字段族。 */
export const THRESHOLD_GROUPS: ThresholdGroup[] = [
  {
    key: 'battery',
    title: '电量',
    description: '电量百分比低于阈值即告警（下限型）。',
    direction: 'lower',
    metrics: [{ prefix: 'batteryPercent', label: '电量百分比', unit: '%' }],
  },
  {
    key: 'cell',
    title: '单体电压',
    description: '单体电压低于阈值即告警（下限型）。',
    direction: 'lower',
    metrics: [{ prefix: 'cellVoltage', label: '单体电压', unit: 'V' }],
  },
  {
    key: 'batteryTemp',
    title: '电池温度',
    description: '电池温度高于阈值即告警（上限型）。',
    direction: 'upper',
    metrics: [{ prefix: 'batteryTemp', label: '电池温度', unit: '°C' }],
  },
  {
    key: 'satellites',
    title: '卫星定位',
    description: '可见卫星数低于阈值即告警（下限型）。',
    direction: 'lower',
    metrics: [{ prefix: 'satellites', label: '可见卫星数', unit: '颗' }],
  },
  {
    key: 'hdop',
    title: '定位精度',
    description: 'HDOP 高于阈值即告警（上限型）。',
    direction: 'upper',
    metrics: [{ prefix: 'hdop', label: 'HDOP', unit: '' }],
  },
  {
    key: 'opticalFlow',
    title: '光流质量',
    description: '光流质量低于阈值即告警（下限型）。',
    direction: 'lower',
    metrics: [{ prefix: 'opticalFlow', label: '光流质量', unit: '' }],
  },
  {
    key: 'link',
    title: '链路',
    description: '链路质量低于阈值即告警（下限型）。',
    direction: 'lower',
    metrics: [{ prefix: 'linkQuality', label: '链路质量', unit: '' }],
  },
  {
    key: 'latency',
    title: '链路延迟',
    description: '链路延迟高于阈值即告警（上限型）。',
    direction: 'upper',
    metrics: [{ prefix: 'latency', label: '链路延迟', unit: 'ms' }],
  },
  {
    key: 'attitude',
    title: '姿态角',
    description: '横滚 / 俯仰角超过阈值即告警（上限型）。',
    direction: 'upper',
    metrics: [{ prefix: 'attitude', label: '横滚 / 俯仰角', unit: '°' }],
  },
];

/** 单个指标族的「注意/警告/严重」单调性要求，随方向反转。 */
export function orderingRule(direction: ThresholdDirection): { symbols: [string, string]; hint: string } {
  return direction === 'lower'
    ? { symbols: ['≥', '≥'], hint: '数值越大越宽松' }
    : { symbols: ['≤', '≤'], hint: '数值越小越宽松' };
}

/**
 * 校验每个指标族的单调性：下限型要求 注意 ≥ 警告 ≥ 严重，上限型反过来（注意 ≤ 警告 ≤ 严重）。
 * 不满足时同一时刻会同时命中多个等级，告警等级就失去意义 —— 所以这里必须按方向分别判定。
 */
export function thresholdOrderingProblems(
  thresholds: DroneAlertThresholds,
): { field: string; message: string }[] {
  const problems: { field: string; message: string }[] = [];
  for (const group of THRESHOLD_GROUPS) {
    const { symbols } = orderingRule(group.direction);
    for (const metric of group.metrics) {
      const caution = thresholds[`${metric.prefix}Caution` as ThresholdKey];
      const warning = thresholds[`${metric.prefix}Warning` as ThresholdKey];
      const critical = thresholds[`${metric.prefix}Critical` as ThresholdKey];
      const finite =
        Number.isFinite(caution) && Number.isFinite(warning) && Number.isFinite(critical);
      const ordered =
        finite &&
        (group.direction === 'lower'
          ? caution >= warning && warning >= critical
          : caution <= warning && warning <= critical);
      if (!ordered) {
        problems.push({
          field: `${metric.prefix}Caution`,
          message: `${metric.label}需满足 注意 ${symbols[0]} 警告 ${symbols[1]} 严重`,
        });
      }
    }
  }
  return problems;
}
